import PNFO from 'jlinc-shared/PNFO'
import { h, Fragment } from 'preact'
import { useState, useCallback, useMemo } from 'preact/hooks'

import { useLocationQueryState } from 'resources/location'
import { useOrganizationFeedSubscriptions } from 'lib/feedSubscriptionHooks'
import { useOrganizations } from 'lib/membershipAppStateHooks'
import { useCreatePageErrorIf } from 'resources/pageAlerts'

import Loading from 'components/Loading'
import Button from 'components/Button'
import Header from 'components/Header'
import PageTabs from 'components/PageTabs'
import ErrorMessage from 'components/ErrorMessage'
import { hrefToOrganization } from 'components/LinkToOrganization'
import OrganizationList from 'components/OrganizationList'
import OrganizationSearch from 'components/OrganizationSearch'
import MyOrganizationsList from 'components/MyOrganizationsList'
import ManageOrganizationFeedSubscriptionsModal from 'components/ManageOrganizationFeedSubscriptionsModal'

export default function Subscriptions({ organization }){
  const {
    organizationFeedSubscriptions,
    errorLoadingOrganizationFeedSubscriptions,
    loadingOrganizationFeedSubscriptions,
    errorSubscribingToOrganizationFeed: errorSubscribing,
    errorUnsubscribingFromOrganizationFeed: errorUnsubscribing,
  } = useOrganizationFeedSubscriptions(
    organization.apikey,
    'OrganizationAdminPage/SubscriptionsPage'
  )

  useCreatePageErrorIf(errorSubscribing, `Failed to subscribe. Please try again later.`)
  useCreatePageErrorIf(errorUnsubscribing, `Failed to unsubscribe. Please try again later.`)

  // the source hub whose channels we're mapping into ours — opens the shared,
  // topic-aware subscribe modal (same one used from a hub's forum page)
  const [modalSourceOrganization, setModalSourceOrganization] = useState(null)

  // source hubs currently feeding this hub's forum (subscriber = this hub)
  const sourceApikeys = useMemo(
    () => [...new Set(
      organizationFeedSubscriptions
        .filter(s => s.subscriberOrganizationApikey === organization.apikey && s.organizationApikey)
        .map(s => s.organizationApikey)
    )],
    [organizationFeedSubscriptions, organization.apikey]
  )

  const listPagePathname = `${hrefToOrganization(organization.apikey)}/admin/subscriptions`
  const props = {
    organization,
    sourceApikeys,
    listPagePathname,
    findPagePathname: `${listPagePathname}/find`,
    openSubscribeModal: setModalSourceOrganization,
  }

  return <div className="OrganizationAdminPage-Subscriptions">
    <ErrorMessage error={errorLoadingOrganizationFeedSubscriptions}/>
    {loadingOrganizationFeedSubscriptions
      ? <Loading type="block" />
      : <PageTabs {...{
        size: 'lg',
        padded: true,
        tabs: [
          {
            title: 'Subscribed to',
            href: props.listPagePathname,
            render: () => <SubscribedTo {...props}/>,
          },
          {
            title: `Find ${PNFO.plural}`,
            href: props.findPagePathname,
            render: () => <FindMore {...props}/>,
          },
        ],
      }}/>
    }
    {modalSourceOrganization &&
      <ManageOrganizationFeedSubscriptionsModal {...{
        open: true,
        organization: modalSourceOrganization,
        onClose: () => setModalSourceOrganization(null),
      }}/>
    }
  </div>
}

function SubscribedTo({ organization, sourceApikeys, findPagePathname, openSubscribeModal }){
  const { organizations, organizationsLoading } = useOrganizations(
    sourceApikeys,
    'OrganizationAdminPage/SubscriptionsPage'
  )

  const rows = (organizations || []).map(sourceOrganization => ({
    ...sourceOrganization,
    buttons: [
      <Button {...{
        type: 'normal',
        value: 'Edit subscriptions',
        onClick: () => openSubscribeModal(sourceOrganization),
      }}/>,
    ],
  }))

  return <Fragment>
    <div className="OrganizationAdminPage-padded">
      <Header size="lg">{PNFO.plural} that feed {organization.name}'s Forum.</Header>
      <p>
        Feed posts from these {PNFO.plural}' channels are automatically imported
        into {organization.name}'s Forum channels.
      </p>
    </div>
    <OrganizationList {...{
      organizations: rows,
      organizationsLoading,
      showMembershipPills: false,
      onEmpty: <NoSubscriptions/>,
    }}/>
    <Header size="lg" centered>
      <Button type="primary" href={findPagePathname}>
        Feed {organization.name} with more {PNFO.plural}!
      </Button>
    </Header>
  </Fragment>
}

function FindMore({ organization, openSubscribeModal }){
  const [query, setQuery] = useLocationQueryState(
    {query: 'q', defaultValue: '', replace: true},
    'OrganizationAdminPage/SubscriptionsPage'
  )
  const searching = !!(query && query.trim())
  const filter = useCallback(o => o.apikey !== organization.apikey, [organization.apikey])
  const map = otherOrganization => ({
    ...otherOrganization,
    buttons: [
      <Button {...{
        type: 'primary',
        value: 'Subscribe',
        onClick: () => openSubscribeModal(otherOrganization),
      }}/>,
    ],
  })
  return <Fragment>
    <div className="OrganizationAdminPage-padded">
      <Header subtle size="sm">
        Search for {PNFO.plural} to feed {organization.name}'s Forum
      </Header>
    </div>
    <OrganizationSearch {...{
      query,
      onChange: setQuery,
      placeholder: 'Search…',
      autoFocus: true,
      showMembershipPills: false,
      filter,
      map,
    }}/>
    {searching || <MyOrganizationsList {...{ filter, map, showMembershipPills: false }} />}
  </Fragment>
}

const NoSubscriptions = () =>
  <span>Use the “Find {PNFO.plural}” tab to subscribe to other {PNFO.plural}</span>
